import React, { useState, useEffect } from "react";
import { Table, Modal, Select, Button, message, Popconfirm, Input, DatePicker, Row, Col, Card, Space, Tag } from "antd";
import { AiFillDelete, AiOutlineSearch, AiOutlineClear } from "react-icons/ai";
import { FileExcelOutlined } from "@ant-design/icons";
import axios from "axios";
import { base_url } from "../utils/baseUrl";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import watermark from "../images/watermark.png"
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import * as XLSX from "xlsx";

dayjs.extend(isBetween);

const { Option } = Select;
const { RangePicker } = DatePicker;

const gstRates = {
    "Covers": { gst: 5, hsn: "6304" },
    "Pillow & Cushion": { gst: 18, hsn: "9404" },
    "Comforter": { gst: 5, hsn: "9404" },
    "Mattress & Pillow Protector": { gst: 5, hsn: "9404" },
    "Dohar": { gst: 5, hsn: "6304" },
    "Bedsheet": { gst: 5, hsn: "6304" },
};

const Orders = () => {
    const [orders, setOrders] = useState([]);
    const [orderDetails, setOrderDetails] = useState([]);
    const [currentOrder, setCurrentOrder] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);
    const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const backend = "https://backend.gulabidecor.com";
    const navigate = useNavigate();

    // Filter states
    const [filters, setFilters] = useState({
        orderId: "",
        customerName: "",
        status: null,
        paymentMethod: null,
        dateRange: null,
        minAmount: null,
        maxAmount: null,
        discountType: null,
        state: "",
        city: "",
        contactNumber: "",
    });

    useEffect(() => {
        const adminToken = localStorage.getItem("adminToken");
        if (!adminToken) {
            navigate("/");
        }
    }, [navigate]);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            const response = await axios.get(`${base_url}/api/orders/getAllOrders`);
            const sortedOrders = response.data.sort((a, b) => b.order_id - a.order_id);
            setOrders(sortedOrders);
        } catch (err) {
            console.error("Error fetching orders:", err.message);
        }
    };

    const fetchOrderDetails = async (orderId) => {
        try {
            const response = await axios.get(`${base_url}/api/orders/${orderId}`);
            setOrderDetails(response.data.items);
            setCurrentOrder(response.data);
            setIsModalOpen(true);
        } catch (error) {
            console.error("Error fetching order details:", error);
            message.error("Failed to fetch order details.");
        }
    };

    const handleStatusChange = async (value, orderId) => {
        try {
            await axios.put(`${base_url}/api/orders/${orderId}`, {
                order_id: orderId,
                status: value,
            });
            setOrders((prevOrders) =>
                prevOrders.map((order) =>
                    order.order_id === orderId ? { ...order, status: value } : order
                )
            );
            message.success("Order status updated successfully");
        } catch (error) {
            console.error("Error updating status:", error);
            message.error("Failed to update status");
        }
    };

    const handleDeleteOrder = async (orderId) => {
        try {
            await axios.delete(`${base_url}/api/orders/${orderId}`);
            setOrders((prevOrders) =>
                prevOrders.filter((order) => order.order_id !== orderId)
            );
            message.success("Order deleted successfully");
        } catch (error) {
            console.error("Error deleting order:", error);
            message.error("Failed to delete order");
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setOrderDetails([]);
        setCurrentOrder(null);
    };

    // Filter function
    const getFilteredOrders = () => {
        return orders.filter((order) => {
            // Order ID filter
            if (filters.orderId && !order.order_id.toString().includes(filters.orderId)) {
                return false;
            }

            // Customer Name filter
            if (filters.customerName && !order.user_name.toLowerCase().includes(filters.customerName.toLowerCase())) {
                return false;
            }

            // Contact Number filter
            if (filters.contactNumber) {
                const phoneValue = (order.phone || "").toString();
                if (!phoneValue.includes(filters.contactNumber)) {
                    return false;
                }
            }

            // Status filter
            if (filters.status && order.status !== filters.status) {
                return false;
            }

            // Payment Method filter
            if (filters.paymentMethod && order.payment_method !== filters.paymentMethod) {
                return false;
            }

            // Date Range filter
            if (filters.dateRange && filters.dateRange.length === 2) {
                const orderDate = dayjs(order.created_at);
                const [startDate, endDate] = filters.dateRange;
                if (!orderDate.isBetween(startDate, endDate, 'day', '[]')) {
                    return false;
                }
            }

            // Amount Range filter
            const totalAmount = parseFloat(order.total_amount);
            if (filters.minAmount !== null && totalAmount < parseFloat(filters.minAmount)) {
                return false;
            }
            if (filters.maxAmount !== null && totalAmount > parseFloat(filters.maxAmount)) {
                return false;
            }

            // Discount Type filter
            if (filters.discountType) {
                const hasDiscount = order.coupons && order.coupons.length > 0 &&
                    order.coupons.some(c => c.discount_percentage || (c.discount_flat && c.discount_flat > 0));

                if (filters.discountType === 'with_discount' && !hasDiscount) {
                    return false;
                }
                if (filters.discountType === 'no_discount' && hasDiscount) {
                    return false;
                }
                if (filters.discountType === 'percentage' &&
                    !(order.coupons && order.coupons.some(c => c.discount_percentage))) {
                    return false;
                }
                if (filters.discountType === 'flat' &&
                    !(order.coupons && order.coupons.some(c => c.discount_flat && c.discount_flat > 0))) {
                    return false;
                }
            }

            // State filter (exact match)
            if (filters.state && order.shipping_state !== filters.state) {
                return false;
            }

            // City filter (exact match)
            if (filters.city && order.shipping_city !== filters.city) {
                return false;
            }

            return true;
        });
    };

    // Clear all filters
    const clearFilters = () => {
        setFilters({
            orderId: "",
            customerName: "",
            status: null,
            paymentMethod: null,
            dateRange: null,
            minAmount: null,
            maxAmount: null,
            discountType: null,
            state: "",
            city: "",
            contactNumber: "",
        });
    };

    // Count active filters
    const getActiveFiltersCount = () => {
        let count = 0;
        if (filters.orderId) count++;
        if (filters.customerName) count++;
        if (filters.status) count++;
        if (filters.paymentMethod) count++;
        if (filters.dateRange) count++;
        if (filters.minAmount !== null || filters.maxAmount !== null) count++;
        if (filters.discountType) count++;
        if (filters.state) count++;
        if (filters.city) count++;
        if (filters.contactNumber) count++;
        return count;
    };

    // Get unique payment methods from orders
    const getUniquePaymentMethods = () => {
        const methods = [...new Set(orders.map(order => order.payment_method))];
        return methods.filter(method => method).sort();
    };

    // Get unique states from orders
    const getUniqueStates = () => {
        const states = [...new Set(orders.map(order => order.shipping_state))];
        return states.filter(state => state).sort();
    };

    // Get unique cities from orders
    const getUniqueCities = () => {
        const cities = [...new Set(orders.map(order => order.shipping_city))];
        return cities.filter(city => city).sort();
    };

    // Helper to calculate flat discount per product
    const getFlatDiscountPerProduct = (item, details, flatDiscount) => {
        const totalItems = details.reduce((sum, detailItem) => sum + (detailItem.quantity || 1), 0);
        if (!totalItems) return 0;
        return (flatDiscount / totalItems) * (item.quantity || 1);
    };

    // Refactored GST and discount calculation
    const calculateTotalGST = (item, order, details) => {
        if (!item || !order) return {};

        const originalPrice = parseFloat(item.price) || 0;
        const quantity = item.quantity || 1;
        let priceAfterDiscount = originalPrice;
        let discountAmount = 0;
        let discountType = null;
        let discountValue = 0;
        let flatDiscount = 0;
        let percentDiscount = 0;

        const coupons = order.coupons || [];

        if (coupons.length > 0) {
            const coupon = coupons[0];
            if (coupon.discount_percentage) {
                percentDiscount = parseFloat(coupon.discount_percentage);
                discountType = "percent";
                discountValue = percentDiscount;
                discountAmount = (originalPrice * percentDiscount) / 100;
                priceAfterDiscount = originalPrice - discountAmount;
            } else if (coupon.discount_flat && coupon.discount_flat > 0) {
                flatDiscount = getFlatDiscountPerProduct(item, details, coupon.discount_flat);
                discountType = "flat";
                discountValue = flatDiscount;
                discountAmount = flatDiscount;
                priceAfterDiscount = originalPrice - flatDiscount / quantity;
            }
        }

        if (percentDiscount && flatDiscount) {
            priceAfterDiscount = originalPrice - (originalPrice * percentDiscount) / 100;
            priceAfterDiscount -= flatDiscount / quantity;
            discountAmount = originalPrice - priceAfterDiscount;
        }

        if (!discountType && item.discount) {
            percentDiscount = parseFloat(item.discount);
            discountType = "percent";
            discountValue = percentDiscount;
            discountAmount = (originalPrice * percentDiscount) / 100;
            priceAfterDiscount = originalPrice - discountAmount;
        }

        const categoryData = gstRates[item.category?.category_name] || {};
        let gstRate = 0;

        const orderDate = new Date(order.created_at);
        const orderDateStr = orderDate.toISOString().split("T")[0];
        const startDateStr = "2025-09-22";
        const isNewTaxApplicable = orderDateStr >= startDateStr;

        if (isNewTaxApplicable) {
            const productPrice = parseFloat(item.price) || 0;
            const isHighValueProduct = productPrice > 2500;

            if (isHighValueProduct) {
                gstRate = 18;
            } else {
                if (typeof categoryData.gst === "function") {
                    gstRate = categoryData.gst(priceAfterDiscount);
                } else if (typeof categoryData.gst === "number") {
                    gstRate = categoryData.gst;
                }
                if (!gstRate) gstRate = 5;
            }
        } else {
            if (typeof categoryData.gst === "function") {
                gstRate = categoryData.gst(priceAfterDiscount);
            } else if (typeof categoryData.gst === "number") {
                gstRate = categoryData.gst;
            }
            if (!gstRate) gstRate = 5;
        }

        const hsnCode = categoryData.hsn || "N/A";
        const principalAmount = (priceAfterDiscount * 100) / (100 + gstRate);
        const gstAmount = principalAmount * (gstRate / 100);

        const isRajasthan = order.shipping_state && order.shipping_state.toLowerCase() === "rajasthan";
        const cgst = isRajasthan ? gstAmount / 2 : 0;
        const sgst = isRajasthan ? gstAmount / 2 : 0;
        const igst = isRajasthan ? 0 : gstAmount;

        return {
            product: item.product_name,
            originalPrice: originalPrice.toFixed(2),
            discountAmount: discountAmount.toFixed(2),
            basePrice: principalAmount.toFixed(2),
            gstAmount: gstAmount.toFixed(2),
            cgst: cgst.toFixed(2),
            sgst: sgst.toFixed(2),
            igst: igst.toFixed(2),
            hsn: hsnCode,
            finalPrice: priceAfterDiscount.toFixed(2),
            discountType,
            discountValue,
        };
    };

    const getFormattedDiscount = (order) => {
        if (!order?.coupons || order.coupons.length === 0) return "No Discount";
        return order.coupons
            .map((coupon) => {
                if (coupon.discount_flat === 0 && coupon.description) {
                    return coupon.description;
                }

                if (coupon.discount_percentage) {
                    return `${coupon.discount_percentage}% off`;
                } else if (coupon.discount_flat > 0) {
                    return `₹${coupon.discount_flat} off`;
                } else {
                    return coupon.description || "No Discount";
                }
            })
            .join(", ");
    };

    const generateInvoicePdf = async (order, details) => {
        const doc = new jsPDF("p", "mm", "a4");

        const pageWidth = doc.internal.pageSize.getWidth();
        const imgWidth = 100;
        const imgHeight = 120;
        const yPos = 40;
        const xPos = pageWidth - imgWidth - 20;

        doc.addImage(watermark, "PNG", xPos, yPos, imgWidth, imgHeight, "", "NONE", 0.1);

        let y = 16;
        doc.setFont("helvetica", "bold");
        doc.setTextColor(107, 63, 79);
        doc.setFontSize(16);
        doc.text("TRIVENI INTERNATIONAL", 10, y);
        y += 6;
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "normal");
        doc.text("F868, VKIA Road no 14, Jaipur, 302013", 10, y);
        y += 5;
        doc.text("+91 9928477794 | Email: info@gulabidecor.com", 10, y);
        y += 5;
        doc.text("GST Number: 08AAWFT3566C1ZQ", 10, y);
        y += 7;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text(`Invoice ID: ${new Date().getFullYear()}${order.order_id}`, 10, y);
        y += 6;
        doc.setDrawColor(0);
        doc.setLineWidth(0.2);
        doc.line(8, y, 202, y);
        y += 4;
        doc.setFont("helvetica", "bold");
        const paymentMethodDisplay = order.payment_method === 'partial_cod'
            ? 'Partial COD'
            : order.payment_method.toUpperCase();
        doc.text(`Order ID: #${order.order_id} (${paymentMethodDisplay})`, 10, y);
        y += 6;
        doc.setFont("helvetica", "normal");
        doc.text(`Order Date: ${new Date(order.created_at).toLocaleDateString()}`, 10, y);
        y += 6;
        doc.text(`Customer Name: ${order.user_name}`, 10, y);
        y += 6;
        doc.text("Customer Details:", 10, y);
        y += 5;
        const formattedAddress = `${order.shipping_address}, ${order.shipping_city}, ${order.shipping_state}, ${order.shipping_postal_code}, ${order.shipping_country}`.replace(/\s+/g, " ").trim();
        doc.text(formattedAddress, 10, y);
        y += 6;
        doc.setFont("helvetica", "bold");
        doc.setTextColor(133, 100, 4);
        doc.setFontSize(10);
        const phoneText = `Contact Number: ${order.phone}`;
        const phoneWidth = doc.getTextWidth(phoneText);
        doc.setFillColor(255, 243, 205);
        doc.roundedRect(10, y - 4, phoneWidth + 4, 7, 1, 1, "F");
        doc.text(phoneText, 12, y);
        y += 6;
        doc.setFont("helvetica", "normal");
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(9);
        doc.text(`Email: ${order.email}`, 10, y);

        const loadProductImages = () => {
            const imagePromises = details.map((item) => {
                return new Promise((resolve) => {
                    const img = new Image();
                    img.crossOrigin = "Anonymous";
                    const imageUrl = `${backend}/${item.product_image}`;

                    img.onload = () => {
                        resolve({
                            item,
                            image: img,
                            success: true,
                        });
                    };

                    img.onerror = () => {
                        console.error(`Failed to load image for ${item.product_name}`);
                        resolve({
                            item,
                            image: null,
                            success: false,
                        });
                    };

                    img.src = imageUrl;
                });
            });

            return Promise.all(imagePromises);
        };

        const loadedImages = await loadProductImages();
        const imageMap = {};
        loadedImages.forEach(({ item, image, success }) => {
            if (success) {
                imageMap[item.product_image] = image;
            }
        });

        const isRajasthan = order.shipping_state && order.shipping_state.toLowerCase() === "rajasthan";

        const tableHeaders = ["Product Image", "Product Details", "Price", "Principal Amt."];

        if (isRajasthan) {
            tableHeaders.push("CGST", "SGST");
        } else {
            tableHeaders.push("IGST");
        }

        tableHeaders.push("Final Amt.", "Discount");

        const tableBody = details.map((item, index) => {
            const gstDetails = calculateTotalGST(item, order, details);
            const quantity = item.quantity || 1;
            const finalAmount = parseFloat(gstDetails.finalPrice) * quantity;
            const row = [
                "",
                `${item.product_name}\n${item.selected_size || "N/A"} | Qty-${quantity}${item.category?.category_name === "Bedsheet" ? ` | ${item.is_Fitted}` : ""}\nHSN: ${gstDetails.hsn}`,
                `INR ${(parseFloat(item.price) * quantity).toFixed(2)}`,
                `INR ${(parseFloat(gstDetails.basePrice) * quantity).toFixed(2)}`,
            ];
            if (isRajasthan) {
                row.push(
                    `INR ${(parseFloat(gstDetails.cgst) * quantity).toFixed(2)}`,
                    `INR ${(parseFloat(gstDetails.sgst) * quantity).toFixed(2)}`
                );
            } else {
                row.push(`INR ${(parseFloat(gstDetails.igst) * quantity).toFixed(2)}`);
            }
            row.push(`INR ${finalAmount.toFixed(2)}`);
            if (index === 0) {
                row.push({
                    content: getFormattedDiscount(order).replace(/₹/g, "INR "),
                    rowSpan: details.length,
                    styles: { valign: "middle", halign: "center", fontStyle: "bold" },
                });
            } else {
                row.push("");
            }
            while (row.length < tableHeaders.length) row.push("");
            return row;
        });

        autoTable(doc, {
            startY: 80,
            margin: { left: 8, right: 8, top: 8, bottom: 8 },
            headStyles: {
                fillColor: [107, 63, 79],
                textColor: [255, 255, 255],
                halign: "center",
                valign: "middle",
                fontSize: 10,
            },
            head: [tableHeaders],
            body: tableBody,
            didDrawCell: (data) => {
                if (data.column.index === 0 && data.cell.section === "body") {
                    const item = details[data.row.index];
                    const image = imageMap[item.product_image];

                    if (image) {
                        const cellHeight = data.cell.height;
                        const cellWidth = data.cell.width;
                        const padding = 2;

                        const maxDim = Math.min(cellHeight, cellWidth) - padding * 2;
                        const x = data.cell.x + (cellWidth - maxDim) / 2;
                        const y = data.cell.y + (cellHeight - maxDim) / 2;

                        try {
                            const canvas = document.createElement("canvas");
                            canvas.width = image.width;
                            canvas.height = image.height;
                            const ctx = canvas.getContext("2d");
                            ctx.drawImage(image, 0, 0);
                            const dataUrl = canvas.toDataURL("image/jpeg");
                            doc.addImage(dataUrl, "JPEG", x, y, maxDim, maxDim);
                        } catch (err) {
                            console.error("Error adding image to PDF:", err);
                        }
                    }
                }
            },
            theme: "grid",
            styles: {
                lineWidth: 0.2,
                fontSize: 8,
                cellPadding: 1,
                minCellHeight: 0,
                valign: "middle",
                overflow: "linebreak",
                cellWidth: "wrap",
            },
            footStyles: {
                fillColor: [240, 240, 240],
                textColor: [0, 0, 0],
                fontStyle: "bold",
                halign: "right",
                cellPadding: 1,
                minCellHeight: 0,
                fontSize: 8,
            },
            foot: (() => {
                const footerRows = [];

                footerRows.push([
                    {
                        content: "Subtotal (Before Discount):",
                        colSpan: isRajasthan ? 7 : 6,
                        styles: { halign: "right", fontStyle: "bold" },
                    },
                    {
                        content: `INR ${(details.reduce((sum, item) => sum + (parseFloat(item.price) || 0) * (item.quantity || 1), 0)).toFixed(2)}`,
                        styles: { halign: "center", fontStyle: "bold" },
                    },
                ]);

                if (order.coupons && order.coupons.length > 0) {
                    const applicableDiscounts = order.coupons.filter(
                        (coupon) => coupon.discount_percentage || (coupon.discount_flat && coupon.discount_flat > 0)
                    );

                    if (applicableDiscounts.length > 0) {
                        const discountText = applicableDiscounts
                            .map(
                                (coupon) =>
                                    `Discount Applied (${coupon.code}): ${coupon.discount_percentage
                                        ? `${coupon.discount_percentage}% off`
                                        : coupon.discount_flat > 0
                                            ? `INR ${coupon.discount_flat} off`
                                            : coupon.description || "No Discount"
                                    }`
                            )
                            .join("\n");

                        const discountAmount = applicableDiscounts
                            .map((coupon) => {
                                if (coupon.discount_percentage) {
                                    const productTotal = details.reduce(
                                        (sum, item) => sum + (parseFloat(item.price) || 0) * (item.quantity || 1),
                                        0
                                    );
                                    return `- INR ${(productTotal * parseFloat(coupon.discount_percentage) / 100).toFixed(2)}`;
                                } else if (coupon.discount_flat > 0) {
                                    return `- INR ${parseFloat(coupon.discount_flat).toFixed(2)}`;
                                }
                                return "N/A";
                            })
                            .join("\n");

                        footerRows.push([
                            {
                                content: discountText,
                                colSpan: isRajasthan ? 7 : 6,
                                styles: { halign: "right", fontStyle: "bold" },
                            },
                            {
                                content: discountAmount,
                                styles: { halign: "center", fontStyle: "bold" },
                            },
                        ]);
                    }
                }

                // COD Charges for normal COD
                if (order.payment_method === "cod" && order.cod_fee > 0) {
                    footerRows.push([
                        {
                            content: "COD Charges:",
                            colSpan: isRajasthan ? 7 : 6,
                            styles: { halign: "right", fontStyle: "bold" },
                        },
                        {
                            content: `INR ${order.cod_fee}`,
                            styles: { halign: "center", fontStyle: "bold" },
                        },
                    ]);
                }

                // Partial COD details
                if (order.payment_method === "partial_cod") {
                    const advancePaid = parseFloat(order.payment_online_offer || 0);
                    const codCharges = parseFloat(order.cod_fee || 0);
                    const codBalance = parseFloat(order.total_amount) - advancePaid;

                    footerRows.push([
                        {
                            content: "Advance Paid (Online):",
                            colSpan: isRajasthan ? 7 : 6,
                            styles: { halign: "right", fontStyle: "bold", textColor: [0, 128, 0] },
                        },
                        {
                            content: `INR ${advancePaid.toFixed(2)}`,
                            styles: { halign: "center", fontStyle: "bold", textColor: [0, 128, 0] },
                        },
                    ]);

                    // Only show COD Charges if cod_fee > 0
                    if (codCharges > 0) {
                        footerRows.push([
                            {
                                content: "COD Charges:",
                                colSpan: isRajasthan ? 7 : 6,
                                styles: { halign: "right", fontStyle: "bold" },
                            },
                            {
                                content: `INR ${codCharges.toFixed(2)}`,
                                styles: { halign: "center", fontStyle: "bold" },
                            },
                        ]);
                    }

                    footerRows.push([
                        {
                            content: "Remaining Amount (Pay on Delivery):",
                            colSpan: isRajasthan ? 7 : 6,
                            styles: { halign: "right", fontStyle: "bold", textColor: [255, 107, 53] },
                        },
                        {
                            content: `INR ${codBalance.toFixed(2)}`,
                            styles: { halign: "center", fontStyle: "bold", textColor: [255, 107, 53] },
                        },
                    ]);
                }

                footerRows.push([
                    {
                        content: "Total Amount:",
                        colSpan: isRajasthan ? 7 : 6,
                        styles: { halign: "right", fontStyle: "bold" },
                    },
                    {
                        content: `INR ${order.total_amount}`,
                        styles: { halign: "center", fontStyle: "bold" },
                    },
                ]);

                return footerRows;
            })(),
        });

        let termsY = doc.lastAutoTable.finalY + 10;
        if (termsY + 40 > 280) {
            doc.addPage();
            termsY = 20;
        }
        doc.setFont("helvetica", "bold");
        doc.text("Terms & Conditions:", 14, termsY);
        doc.setFont("helvetica", "normal");
        doc.text("- Products can only be returned if footage is shared via email.", 14, termsY + 10);
        doc.text("- Accept the package only if the Gulabi Decor logo is embedded on the cover.", 14, termsY + 20);
        doc.text("- Payment is processed via Razorpay for both COD and Prepaid orders.", 14, termsY + 30);
        doc.text("- ShipRocket is the official shipping partner.", 14, termsY + 40);

        doc.save(`Order_${order.order_id}_Invoice.pdf`);
    };

    const downloadInvoice = async () => {
        if (!currentOrder) {
            message.error("No order selected.");
            return;
        }

        try {
            await generateInvoicePdf(currentOrder, orderDetails);
        } catch (error) {
            console.error("Error generating PDF with images:", error);
            message.error("Error generating PDF with images");
        }
    };

    const handleBulkInvoiceDownload = async () => {
        if (selectedOrderIds.length === 0) {
            message.warning("Please select at least one order.");
            return;
        }

        setBulkDownloadLoading(true);
        try {
            for (const orderId of selectedOrderIds) {
                const response = await axios.get(`${base_url}/api/orders/${orderId}`);
                const orderData = response.data;
                const items = orderData.items || [];
                await generateInvoicePdf(orderData, items);
            }
            message.success(`Invoices downloaded for ${selectedOrderIds.length} order(s).`);
        } catch (error) {
            console.error("Bulk invoice download failed:", error);
            message.error("Failed to download some invoices. Please try again.");
        } finally {
            setBulkDownloadLoading(false);
        }
    };

    // Excel Export Function
    const exportToExcel = async () => {
        if (filteredOrders.length === 0) {
            message.warning("No orders to export.");
            return;
        }

        setExportLoading(true);
        try {
            // Prepare data for export
            const exportData = [];

            // Add header row
            exportData.push([
                "Order ID",
                "Order Date",
                "Customer Name",
                "Contact Number",
                "Email",
                "Shipping Address",
                "City",
                "State",
                "Postal Code",
                "Country",
                "Total Amount",
                "Status",
                "Payment Method",
                "COD Fee",
                "Advance Paid (Online)",
                "Discount Type",
                "Discount Value",
                "Coupon Code",
                "Products",
                "Product Quantities",
                "Product Prices",
                "Created At"
            ]);

            // Process each order
            for (const order of filteredOrders) {
                // Fetch detailed order information
                let orderData = order; // Use order as fallback
                let orderItems = [];
                try {
                    const response = await axios.get(`${base_url}/api/orders/${order.order_id}`);
                    orderData = response.data; // Get full order data
                    orderItems = response.data.items || [];
                } catch (error) {
                    console.error(`Error fetching details for order ${order.order_id}:`, error);
                    // Use original order data if fetch fails
                    orderData = order;
                }

                // Format products information
                const products = orderItems.length > 0
                    ? orderItems.map(item => item.product_name).join("; ")
                    : "N/A";
                const quantities = orderItems.length > 0
                    ? orderItems.map(item => `${item.product_name}: ${item.quantity || 1}`).join("; ")
                    : "N/A";
                const prices = orderItems.length > 0
                    ? orderItems.map(item => `${item.product_name}: ₹${(parseFloat(item.price) || 0).toFixed(2)}`).join("; ")
                    : "N/A";

                // Format discount information
                let discountType = "No Discount";
                let discountValue = "";
                let couponCode = "";

                if (orderData.coupons && orderData.coupons.length > 0) {
                    const coupon = orderData.coupons[0];
                    if (coupon.discount_percentage) {
                        discountType = "Percentage";
                        discountValue = `${coupon.discount_percentage}%`;
                    } else if (coupon.discount_flat && coupon.discount_flat > 0) {
                        discountType = "Flat";
                        discountValue = `₹${coupon.discount_flat}`;
                    } else if (coupon.description) {
                        discountType = "Description";
                        discountValue = coupon.description;
                    }
                    couponCode = coupon.code || "";
                }

                // Format payment method
                const paymentMethod = orderData.payment_method === 'partial_cod'
                    ? 'Partial COD'
                    : orderData.payment_method ? orderData.payment_method.toUpperCase() : 'N/A';

                // Format date
                const orderDate = orderData.created_at
                    ? new Date(orderData.created_at).toLocaleDateString('en-IN', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                    })
                    : 'N/A';

                const createdAt = orderData.created_at
                    ? new Date(orderData.created_at).toLocaleString('en-IN', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                    })
                    : 'N/A';

                // Get email, COD fee, and advance amount from detailed order data
                const email = orderData.email || order.email || "N/A";
                const codFee = orderData.cod_fee || order.cod_fee || 0;
                const advancePaid = orderData.payment_online_offer || order.payment_online_offer || 0;

                // Add order row
                exportData.push([
                    orderData.order_id || order.order_id,
                    orderDate,
                    orderData.user_name || order.user_name || "N/A",
                    orderData.phone || order.phone || "N/A",
                    email,
                    orderData.shipping_address || order.shipping_address || "N/A",
                    orderData.shipping_city || order.shipping_city || "N/A",
                    orderData.shipping_state || order.shipping_state || "N/A",
                    orderData.shipping_postal_code || order.shipping_postal_code || "N/A",
                    orderData.shipping_country || order.shipping_country || "N/A",
                    `₹${parseFloat(orderData.total_amount || order.total_amount || 0).toFixed(2)}`,
                    orderData.status ? orderData.status.charAt(0).toUpperCase() + orderData.status.slice(1) : (order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : "N/A"),
                    paymentMethod,
                    codFee ? `₹${parseFloat(codFee).toFixed(2)}` : "₹0.00",
                    advancePaid ? `₹${parseFloat(advancePaid).toFixed(2)}` : "₹0.00",
                    discountType,
                    discountValue,
                    couponCode,
                    products,
                    quantities,
                    prices,
                    createdAt
                ]);
            }

            // Create workbook
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet(exportData);

            // Set column widths for better readability
            const colWidths = [
                { wch: 10 },  // Order ID
                { wch: 12 },  // Order Date
                { wch: 20 },  // Customer Name
                { wch: 15 },  // Contact Number
                { wch: 25 },  // Email
                { wch: 30 },  // Shipping Address
                { wch: 15 },  // City
                { wch: 15 },  // State
                { wch: 12 },  // Postal Code
                { wch: 12 },  // Country
                { wch: 12 },  // Total Amount
                { wch: 12 },  // Status
                { wch: 15 },  // Payment Method
                { wch: 10 },  // COD Fee
                { wch: 18 },  // Advance Paid
                { wch: 15 },  // Discount Type
                { wch: 15 },  // Discount Value
                { wch: 15 },  // Coupon Code
                { wch: 40 },  // Products
                { wch: 40 },  // Product Quantities
                { wch: 40 },  // Product Prices
                { wch: 20 }   // Created At
            ];
            ws['!cols'] = colWidths;

            // Freeze header row for better navigation
            ws['!freeze'] = { x: 0, y: 1 };

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, "Orders");

            // Generate filename with timestamp
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
            const filename = `Orders_Export_${timestamp}.xlsx`;

            // Write file
            XLSX.writeFile(wb, filename);

            message.success(`Successfully exported ${filteredOrders.length} order(s) to Excel.`);
        } catch (error) {
            console.error("Error exporting to Excel:", error);
            message.error("Failed to export orders to Excel. Please try again.");
        } finally {
            setExportLoading(false);
        }
    };

    // CSV Export Function (Alternative format)
    const exportToCSV = async () => {
        if (filteredOrders.length === 0) {
            message.warning("No orders to export.");
            return;
        }

        setExportLoading(true);
        try {
            // Prepare CSV data
            let csvContent = "Order ID,Order Date,Customer Name,Contact Number,Email,Shipping Address,City,State,Postal Code,Country,Total Amount,Status,Payment Method,COD Fee,Advance Paid,Discount Type,Discount Value,Coupon Code,Products,Product Quantities,Product Prices,Created At\n";

            // Process each order with detailed information
            for (const order of filteredOrders) {
                // Fetch detailed order information
                let orderData = order; // Use order as fallback
                let orderItems = [];
                try {
                    const response = await axios.get(`${base_url}/api/orders/${order.order_id}`);
                    orderData = response.data; // Get full order data
                    orderItems = response.data.items || [];
                } catch (error) {
                    console.error(`Error fetching details for order ${order.order_id}:`, error);
                    // Use original order data if fetch fails
                    orderData = order;
                }

                // Format products information
                const products = orderItems.length > 0
                    ? orderItems.map(item => item.product_name).join("; ")
                    : "N/A";
                const quantities = orderItems.length > 0
                    ? orderItems.map(item => `${item.product_name}: ${item.quantity || 1}`).join("; ")
                    : "N/A";
                const prices = orderItems.length > 0
                    ? orderItems.map(item => `${item.product_name}: ₹${(parseFloat(item.price) || 0).toFixed(2)}`).join("; ")
                    : "N/A";

                // Format discount information
                let discountType = "No Discount";
                let discountValue = "";
                let couponCode = "";

                if (orderData.coupons && orderData.coupons.length > 0) {
                    const coupon = orderData.coupons[0];
                    if (coupon.discount_percentage) {
                        discountType = "Percentage";
                        discountValue = `${coupon.discount_percentage}%`;
                    } else if (coupon.discount_flat && coupon.discount_flat > 0) {
                        discountType = "Flat";
                        discountValue = `₹${coupon.discount_flat}`;
                    } else if (coupon.description) {
                        discountType = "Description";
                        discountValue = coupon.description;
                    }
                    couponCode = coupon.code || "";
                }

                const paymentMethod = orderData.payment_method === 'partial_cod'
                    ? 'Partial COD'
                    : orderData.payment_method ? orderData.payment_method.toUpperCase() : 'N/A';

                const orderDate = orderData.created_at
                    ? new Date(orderData.created_at).toLocaleDateString('en-IN')
                    : 'N/A';

                // Get email, COD fee, and advance amount from detailed order data
                const email = orderData.email || order.email || "N/A";
                const codFee = orderData.cod_fee || order.cod_fee || 0;
                const advancePaid = orderData.payment_online_offer || order.payment_online_offer || 0;

                const row = [
                    orderData.order_id || order.order_id,
                    orderDate,
                    `"${((orderData.user_name || order.user_name || "N/A").replace(/"/g, '""'))}"`,
                    orderData.phone || order.phone || "N/A",
                    email,
                    `"${((orderData.shipping_address || order.shipping_address || "N/A").replace(/"/g, '""'))}"`,
                    orderData.shipping_city || order.shipping_city || "N/A",
                    orderData.shipping_state || order.shipping_state || "N/A",
                    orderData.shipping_postal_code || order.shipping_postal_code || "N/A",
                    orderData.shipping_country || order.shipping_country || "N/A",
                    `₹${parseFloat(orderData.total_amount || order.total_amount || 0).toFixed(2)}`,
                    orderData.status ? orderData.status.charAt(0).toUpperCase() + orderData.status.slice(1) : (order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : "N/A"),
                    paymentMethod,
                    codFee ? `₹${parseFloat(codFee).toFixed(2)}` : "₹0.00",
                    advancePaid ? `₹${parseFloat(advancePaid).toFixed(2)}` : "₹0.00",
                    discountType,
                    discountValue,
                    couponCode,
                    `"${products.replace(/"/g, '""')}"`,
                    `"${quantities.replace(/"/g, '""')}"`,
                    `"${prices.replace(/"/g, '""')}"`,
                    orderData.created_at ? new Date(orderData.created_at).toLocaleString('en-IN') : (order.created_at ? new Date(order.created_at).toLocaleString('en-IN') : 'N/A')
                ].join(",");

                csvContent += row + "\n";
            }

            // Create blob and download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);

            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
            link.setAttribute("download", `Orders_Export_${timestamp}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success(`Successfully exported ${filteredOrders.length} order(s) to CSV.`);
        } catch (error) {
            console.error("Error exporting to CSV:", error);
            message.error("Failed to export orders to CSV. Please try again.");
        } finally {
            setExportLoading(false);
        }
    };

    const columns = [
        {
            title: "Order ID",
            dataIndex: "order_id",
            render: (text) => (
                <Button type="link" onClick={() => fetchOrderDetails(text)}>
                    #{text}
                </Button>
            ),
        },
        {
            title: "Name",
            dataIndex: "user_name",
        },
        {
            title: "Contact Number",
            dataIndex: "phone",
            render: (text) => (
                <span style={{
                    backgroundColor: '#fff3cd',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    color: '#856404',
                    display: 'inline-block'
                }}>
                    {text || 'N/A'}
                </span>
            ),
        },
        {
            title: "Total Amount",
            dataIndex: "total_amount",
            render: (text, record) => (
                <span>
                    ₹{text}
                    {record.payment_method === 'cod' && record.cod_fee > 0 && (
                        <span style={{ color: '#666', fontSize: '12px', marginLeft: '5px' }}>
                            (incl. ₹{record.cod_fee} COD)
                        </span>
                    )}
                </span>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (text, record) => (
                <Select
                    defaultValue={text}
                    onChange={(value) => handleStatusChange(value, record.order_id)}
                    style={{ width: 120 }}
                >
                    <Option value="pending">Pending</Option>
                    <Option value="processed">Processed</Option>
                    <Option value="shipped">Shipped</Option>
                    <Option value="delivered">Delivered</Option>
                    <Option value="cancelled">Cancelled</Option>
                </Select>
            ),
        },
        {
            title: "Payment Method",
            dataIndex: "payment_method",
            render: (text, record) => (
                <div>
                    <div style={{ textTransform: 'capitalize' }}>{text}</div>
                    {record.payment_method === 'cod' && record.cod_fee > 0 && (
                        <div style={{
                            color: '#ff6b35',
                            fontSize: '11px',
                            marginTop: '2px'
                        }}>
                            +₹{record.cod_fee} COD Fee
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: "Discount",
            dataIndex: "discount",
            render: (_, record) => {
                const discounts = [];

                // Add coupon discounts
                if (record.coupons && record.coupons.length > 0) {
                    record.coupons.forEach(coupon => {
                        if (coupon.discount_flat === 0 && coupon.description) {
                            discounts.push(coupon.description);
                        } else {
                            discounts.push(coupon.discount_percentage
                                ? `${coupon.discount_percentage}% off`
                                : coupon.discount_flat > 0
                                    ? `₹${coupon.discount_flat} off`
                                    : coupon.description || "No Discount");
                        }
                    });
                }


                return discounts.length > 0 ? discounts.join(", ") : "No Discount";
            }
        },
        {
            title: "Shipping Address",
            dataIndex: "shipping_address",
        },
        {
            title: "Date",
            dataIndex: "created_at",
        },
        {
            title: "Actions",
            render: (_, record) => (
                <>
                    <Popconfirm
                        title="Are you sure to delete this order?"
                        onConfirm={() => handleDeleteOrder(record.order_id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <AiFillDelete className="action-icon text-danger" />
                    </Popconfirm>
                </>
            ),
        },
    ];

    const filteredOrders = getFilteredOrders();

    const data = filteredOrders.map((order) => ({
        key: order.order_id,
        order_id: order.order_id,
        name: order.user_name,
        user_name: order.user_name,
        coupon: order.coupon,  // Make sure coupon data is mapped
        total_amount: order.total_amount,
        status: order.status,
        payment_method: order.payment_method,
        cod_fee: order.cod_fee || 0,
        payment_online_offer: order.payment_online_offer || 0,
        coupons: order.coupons || [],
        phone: order.phone || 'N/A',
        shipping_address: `${order.shipping_address}, ${order.shipping_city}, ${order.shipping_state}, ${order.shipping_postal_code}, ${order.shipping_country}`,
        created_at: order.created_at ? new Date(order.created_at).toLocaleString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        }) : 'N/A',
    }));

    const rowSelection = {
        selectedRowKeys: selectedOrderIds,
        onChange: (selectedKeys) => setSelectedOrderIds(selectedKeys.map((key) => Number(key))),
        preserveSelectedRowKeys: true,
    };

    return (
        <div>
            <h3 className="mb-4 title">Orders</h3>

            {/* Filters Section */}
            <Card
                title={
                    <Space>
                        <AiOutlineSearch style={{ fontSize: '20px' }} />
                        <span>Filters</span>
                        {getActiveFiltersCount() > 0 && (
                            <Tag color="blue">{getActiveFiltersCount()} Active</Tag>
                        )}
                    </Space>
                }
                extra={
                    <Button
                        icon={<AiOutlineClear />}
                        onClick={clearFilters}
                        disabled={getActiveFiltersCount() === 0}
                    >
                        Clear All
                    </Button>
                }
                style={{ marginBottom: '20px' }}
            >
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Order ID
                        </label>
                        <Input
                            placeholder="Search Order ID"
                            value={filters.orderId}
                            onChange={(e) => setFilters({ ...filters, orderId: e.target.value })}
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Customer Name
                        </label>
                        <Input
                            placeholder="Search Customer Name"
                            value={filters.customerName}
                            onChange={(e) => setFilters({ ...filters, customerName: e.target.value })}
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Status
                        </label>
                        <Select
                            placeholder="Select Status"
                            value={filters.status}
                            onChange={(value) => setFilters({ ...filters, status: value })}
                            style={{ width: '100%' }}
                            allowClear
                        >
                            <Option value="pending">Pending</Option>
                            <Option value="processed">Processed</Option>
                            <Option value="shipped">Shipped</Option>
                            <Option value="delivered">Delivered</Option>
                            <Option value="cancelled">Cancelled</Option>
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Payment Method
                        </label>
                        <Select
                            placeholder="Select Payment Method"
                            value={filters.paymentMethod}
                            onChange={(value) => setFilters({ ...filters, paymentMethod: value })}
                            style={{ width: '100%' }}
                            allowClear
                        >
                            {getUniquePaymentMethods().map(method => (
                                <Option key={method} value={method}>
                                    {method.charAt(0).toUpperCase() + method.slice(1)}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Date Range
                        </label>
                        <RangePicker
                            value={filters.dateRange}
                            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
                            style={{ width: '100%' }}
                            format="DD-MM-YYYY"
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Min Amount (₹)
                        </label>
                        <Input
                            type="number"
                            placeholder="Min Amount"
                            value={filters.minAmount}
                            onChange={(e) => setFilters({ ...filters, minAmount: e.target.value ? parseFloat(e.target.value) : null })}
                            min={0}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Max Amount (₹)
                        </label>
                        <Input
                            type="number"
                            placeholder="Max Amount"
                            value={filters.maxAmount}
                            onChange={(e) => setFilters({ ...filters, maxAmount: e.target.value ? parseFloat(e.target.value) : null })}
                            min={0}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Discount Type
                        </label>
                        <Select
                            placeholder="Select Discount Type"
                            value={filters.discountType}
                            onChange={(value) => setFilters({ ...filters, discountType: value })}
                            style={{ width: '100%' }}
                            allowClear
                        >
                            <Option value="with_discount">With Discount</Option>
                            <Option value="no_discount">No Discount</Option>
                            <Option value="percentage">Percentage Discount</Option>
                            <Option value="flat">Flat Discount</Option>
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            State
                        </label>
                        <Select
                            showSearch
                            placeholder="Select State"
                            value={filters.state}
                            onChange={(value) => setFilters({ ...filters, state: value, city: "" })}
                            style={{ width: '100%' }}
                            allowClear
                            filterOption={(input, option) =>
                                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {getUniqueStates().map(state => (
                                <Option key={state} value={state}>
                                    {state}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            City
                        </label>
                        <Select
                            showSearch
                            placeholder="Select City"
                            value={filters.city}
                            onChange={(value) => setFilters({ ...filters, city: value })}
                            style={{ width: '100%' }}
                            allowClear
                            filterOption={(input, option) =>
                                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {getUniqueCities().map(city => (
                                <Option key={city} value={city}>
                                    {city}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Contact Number
                        </label>
                        <Input
                            placeholder="Search Contact Number"
                            value={filters.contactNumber}
                            onChange={(e) => setFilters({ ...filters, contactNumber: e.target.value })}
                            allowClear
                        />
                    </Col>
                </Row>
            </Card>

            {/* Results Summary Bar */}
            <div style={{
                marginBottom: '15px',
                padding: '12px 16px',
                backgroundColor: '#fff',
                borderRadius: '4px',
                border: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Space>
                    <span style={{ fontWeight: '500', fontSize: '15px' }}>Orders List</span>
                    <Tag color="green" style={{ fontSize: '13px' }}>
                        Showing {filteredOrders.length} of {orders.length} orders
                    </Tag>
                </Space>
                <Space>
                    {getActiveFiltersCount() > 0 && (
                        <Tag color="blue">{getActiveFiltersCount()} filter{getActiveFiltersCount() > 1 ? 's' : ''} applied</Tag>
                    )}
                    <Button
                        type="default"
                        icon={<FileExcelOutlined />}
                        onClick={exportToExcel}
                        disabled={filteredOrders.length === 0}
                        loading={exportLoading}
                        style={{
                            backgroundColor: '#52c41a',
                            borderColor: '#52c41a',
                            color: '#fff'
                        }}
                    >
                        Export to Excel
                    </Button>
                    <Button
                        type="default"
                        onClick={exportToCSV}
                        disabled={filteredOrders.length === 0}
                        style={{
                            backgroundColor: '#1890ff',
                            borderColor: '#1890ff',
                            color: '#fff'
                        }}
                    >
                        Export to CSV
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleBulkInvoiceDownload}
                        disabled={selectedOrderIds.length === 0}
                        loading={bulkDownloadLoading}
                    >
                        Download Selected Invoices
                    </Button>
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={data}
                rowSelection={rowSelection}
                pagination={{
                    pageSize: 10,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100']
                }}
            />
            {isModalOpen && (
                <Modal
                    title={`Order Details - #${currentOrder.order_id}`}
                    open={isModalOpen}
                    onCancel={handleModalClose}
                    footer={[
                        <Button key="download" onClick={downloadInvoice}>Download Invoice</Button>,
                        <Button key="close" onClick={handleModalClose}>Close</Button>
                    ]}
                    width="60vw"
                    styles={{
                        body: {
                            backgroundImage: `url(${require("../images/watermark.png")})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                        }
                    }}
                >
                    <div style={{
                        background: "rgba(255, 255, 255, 0.85)",
                        padding: "20px",
                        borderRadius: "8px",
                    }}>
                        <h2 style={{ color: "#6B3F4F" }}>TRIVENI INTERNATIONAL</h2>
                        <p><br /><strong>Invoice ID:</strong> {new Date().getFullYear()}{currentOrder.order_id}</p>
                        <p><strong>Address:</strong><br />F868, VKIA Road no 14, Jaipur, 302013
                            <br />+91 9928477794 | Email: info@gulabidecor.com</p>
                        <p><strong>GST Number: </strong>08AAWFT3566C1ZQ</p>
                        <hr />
                        <p><strong>Order ID:</strong> #{currentOrder.order_id} ({currentOrder.payment_method === 'partial_cod' ? 'Partial COD' : currentOrder.payment_method.toUpperCase()})</p>
                        <p><strong>Order Date:</strong> {new Date(currentOrder.created_at).toLocaleDateString()}</p>
                        <p><strong>Customer Name:</strong> {currentOrder.user_name}</p>
                        <p><strong>Contact Number:</strong> <br />
                            <span style={{
                                backgroundColor: '#fff3cd',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                fontWeight: 'bold',
                                color: '#856404',
                                display: 'inline-block',
                                fontSize: '16px',
                                marginTop: '4px'
                            }}>
                                {currentOrder.phone || 'N/A'}
                            </span>
                        </p>
                        <p><strong>Customer Details:</strong> <br />{currentOrder.shipping_address}, {currentOrder.shipping_city}, <br />
                            {currentOrder.shipping_state}, {currentOrder.shipping_postal_code}, {currentOrder.shipping_country} <br />
                            <strong>Email:</strong> {currentOrder.email}</p>
                        <hr />

                        <table border="1" width="100%" style={{ textAlign: "left", borderCollapse: "collapse", border: "1px dotted grey" }}>
                            <thead style={{ borderBottom: "2px dotted grey", backgroundColor: "#6B3F4F", color: "white" }}>
                                <tr>
                                    <th style={{ border: "1px dotted grey" }}>Image</th>
                                    <th style={{ border: "1px dotted grey" }}>Product</th>
                                    <th style={{ border: "1px dotted grey" }}>Price</th>
                                    <th style={{ border: "1px dotted grey" }}>Principal Amt.</th>

                                    {/* Show CGST & SGST only for Rajasthan */}
                                    {currentOrder.shipping_state && currentOrder.shipping_state.toLowerCase() === "rajasthan" && (
                                        <>
                                            <th style={{ border: "1px dotted grey" }}>CGST</th>
                                            <th style={{ border: "1px dotted grey" }}>SGST</th>
                                        </>
                                    )}

                                    {/* Show IGST only for other states */}
                                    {currentOrder.shipping_state && currentOrder.shipping_state.toLowerCase() !== "rajasthan" && (
                                        <th style={{ border: "1px dotted grey" }}>IGST</th>
                                    )}

                                    <th style={{ border: "1px dotted grey" }}>Final Amt.</th>
                                    <th style={{ border: "1px dotted grey" }}>Discount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orderDetails.map((item, index) => {
                                    const gstDetails = calculateTotalGST(item, currentOrder, orderDetails);
                                    const quantity = item.quantity || 1;
                                    const finalAmount = parseFloat(gstDetails.finalPrice) * quantity;
                                    return (
                                        <tr key={`${item.product_name}-${index}`}>
                                            <td style={{ border: "1px dotted grey" }}> <img
                                                src={`${backend}/${item.product_image}`}
                                                alt={item.product_name}
                                                style={{ width: "50px", height: "50px", marginRight: "10px" }}
                                            /></td>
                                            <td style={{ border: "1px dotted grey" }}>
                                                {item.product_name} <br />
                                                {item.selected_size || "N/A"} | Qty-{quantity}
                                                {item.category?.category_name === "Bedsheet" ? ` | ${item.is_Fitted}` : ""}
                                                <br /> HSN: {gstDetails.hsn}
                                            </td>
                                            <td style={{ border: "1px dotted grey", textAlign: "right" }}>
                                                ₹{(parseFloat(item.price) * quantity).toFixed(2)}
                                            </td>
                                            <td style={{ border: "1px dotted grey", textAlign: "right" }}>
                                                ₹{(parseFloat(gstDetails.basePrice) * quantity).toFixed(2)}
                                            </td>

                                            {currentOrder.shipping_state && currentOrder.shipping_state.toLowerCase() === "rajasthan" ? (
                                                <React.Fragment key={`gst-${item.product_name}-${index}`}>
                                                    <td style={{ border: "1px dotted grey", textAlign: "right" }}>
                                                        ₹{(parseFloat(gstDetails.cgst) * quantity).toFixed(2)}
                                                    </td>
                                                    <td style={{ border: "1px dotted grey", textAlign: "right" }}>
                                                        ₹{(parseFloat(gstDetails.sgst) * quantity).toFixed(2)}
                                                    </td>
                                                </React.Fragment>
                                            ) : (
                                                <td style={{ border: "1px dotted grey", textAlign: "right" }}>
                                                    ₹{(parseFloat(gstDetails.igst) * quantity).toFixed(2)}
                                                </td>
                                            )}

                                            <td style={{ border: "1px dotted grey", textAlign: "right" }}>
                                                ₹{finalAmount.toFixed(2)}
                                            </td>
                                            {index === 0 && (
                                                <td style={{ border: "1px dotted grey", textAlign: "center" }}
                                                    rowSpan={orderDetails.length}>
                                                    {currentOrder.coupons && currentOrder.coupons.length > 0 && (
                                                        currentOrder.coupons[0].discount_percentage
                                                            ? `${currentOrder.coupons[0].discount_percentage}% off`
                                                            : currentOrder.coupons[0].discount_flat > 0
                                                                ? `₹${currentOrder.coupons[0].discount_flat} off`
                                                                : currentOrder.coupons[0].description || "No Discount"
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>

                            <tfoot style={{ borderTop: "2px dotted grey" }}>
                                {/* Subtotal Before Discount */}
                                <tr>
                                    <td colSpan={currentOrder.shipping_state && currentOrder.shipping_state.toLowerCase() === "rajasthan" ? 7 : 6}
                                        style={{ textAlign: "right", fontWeight: "bold", border: "1px dotted grey" }}>
                                        Subtotal (Before Discount):</td>
                                    <td style={{ textAlign: "right", border: "1px dotted grey" }}>
                                        ₹{(orderDetails.reduce((sum, item) => sum + (parseFloat(item.price) || 0) * (item.quantity || 1), 0)).toFixed(2)}
                                    </td>
                                </tr>
                                {/* Discount Row: Only show if percentage or flat > 0 */}
                                {currentOrder.coupons && currentOrder.coupons.length > 0 && (
                                    currentOrder.coupons.some(coupon => coupon.discount_percentage || (coupon.discount_flat && coupon.discount_flat > 0)) && (
                                        <tr>
                                            <td colSpan={currentOrder.shipping_state && currentOrder.shipping_state.toLowerCase() === "rajasthan" ? 7 : 6}
                                                style={{ textAlign: "right", fontWeight: "bold", border: "1px dotted grey" }}>
                                                {currentOrder.coupons.map((coupon, idx) => (
                                                    <div key={idx}>
                                                        Discount Applied ({coupon.code}): {coupon.discount_percentage ? `${coupon.discount_percentage}% off` : coupon.discount_flat > 0 ? `₹${coupon.discount_flat} off` : coupon.description || "No Discount"}
                                                    </div>
                                                ))}
                                            </td>
                                            <td style={{ textAlign: "right", border: "1px dotted grey" }}>
                                                {currentOrder.coupons.map((coupon, idx) => (
                                                    <div key={idx}>
                                                        {coupon.discount_flat > 0 || coupon.discount_percentage ? `-₹${coupon.discount_percentage ? ((orderDetails.reduce((sum, item) => sum + (parseFloat(item.price) || 0) * (item.quantity || 1), 0) * parseFloat(coupon.discount_percentage)) / 100).toFixed(2) : parseFloat(coupon.discount_flat).toFixed(2)}` : "N/A"}
                                                    </div>
                                                ))}
                                            </td>
                                        </tr>
                                    )
                                )}
                                {currentOrder.coupons && currentOrder.coupons.filter(coupon => coupon.discount_flat === 0 && coupon.description).length > 0 && (
                                    <tr>
                                        <td colSpan={currentOrder.shipping_state && currentOrder.shipping_state.toLowerCase() === "rajasthan" ? 7 : 6}
                                            style={{ textAlign: "right", fontWeight: "bold", border: "1px dotted grey" }}>
                                            {currentOrder.coupons.filter(coupon => coupon.discount_flat === 0 && coupon.description).map((coupon, idx) => (
                                                <div key={idx}>{coupon.description}</div>
                                            ))}
                                        </td>
                                        <td style={{ textAlign: "right", border: "1px dotted grey" }}></td>
                                    </tr>
                                )}

                                {/* COD Charges Row for normal COD */}
                                {currentOrder.payment_method === 'cod' && currentOrder.cod_fee > 0 && (
                                    <tr>
                                        <td colSpan={currentOrder.shipping_state && currentOrder.shipping_state.toLowerCase() === "rajasthan" ? 7 : 6}
                                            style={{ textAlign: "right", fontWeight: "bold", border: "1px dotted grey" }}>
                                            COD Charges:
                                        </td>
                                        <td style={{ textAlign: "right", border: "1px dotted grey" }}>
                                            ₹{currentOrder.cod_fee}
                                        </td>
                                    </tr>
                                )}

                                {/* Partial COD Details */}
                                {currentOrder.payment_method === 'partial_cod' && (
                                    <>
                                        <tr>
                                            <td colSpan={currentOrder.shipping_state && currentOrder.shipping_state.toLowerCase() === "rajasthan" ? 7 : 6}
                                                style={{ textAlign: "right", fontWeight: "bold", border: "1px dotted grey", color: "#008000" }}>
                                                Advance Paid (Online):
                                            </td>
                                            <td style={{ textAlign: "right", border: "1px dotted grey", color: "#008000", fontWeight: "bold" }}>
                                                ₹{parseFloat(currentOrder.payment_online_offer || 0).toFixed(2)}
                                            </td>
                                        </tr>
                                        {/* Only show COD Charges if cod_fee > 0 */}
                                        {parseFloat(currentOrder.cod_fee || 0) > 0 && (
                                            <tr>
                                                <td colSpan={currentOrder.shipping_state && currentOrder.shipping_state.toLowerCase() === "rajasthan" ? 7 : 6}
                                                    style={{ textAlign: "right", fontWeight: "bold", border: "1px dotted grey" }}>
                                                    COD Charges:
                                                </td>
                                                <td style={{ textAlign: "right", border: "1px dotted grey" }}>
                                                    ₹{parseFloat(currentOrder.cod_fee || 0).toFixed(2)}
                                                </td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td colSpan={currentOrder.shipping_state && currentOrder.shipping_state.toLowerCase() === "rajasthan" ? 7 : 6}
                                                style={{ textAlign: "right", fontWeight: "bold", border: "1px dotted grey", color: "#ff6b35" }}>
                                                Remaining Amount (Pay on Delivery):
                                            </td>
                                            <td style={{ textAlign: "right", border: "1px dotted grey", color: "#ff6b35", fontWeight: "bold" }}>
                                                ₹{(parseFloat(currentOrder.total_amount) - parseFloat(currentOrder.payment_online_offer || 0)).toFixed(2)}
                                            </td>
                                        </tr>
                                    </>
                                )}
                                <tr>
                                    <td colSpan={currentOrder.shipping_state && currentOrder.shipping_state.toLowerCase() === "rajasthan" ? 7 : 6}
                                        style={{ textAlign: "right", fontWeight: "bold", border: "1px dotted grey" }}>
                                        Total Amount:
                                    </td>
                                    <td style={{ textAlign: "right", border: "1px dotted grey" }}>
                                        {currentOrder.total_amount}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>

                        <hr /><br /><br />
                        <h6><strong>Terms & Conditions:</strong></h6>
                        <p>- For return kindly drop a mail with the video of unpacking the product.</p>
                        <p>- Accept the package only if the Gulabi Decor logo is embedded on the cover.</p>
                        <p>- Payment is processed via Razorpay for both COD and Prepaid orders (including NetBanking, UPI, and other payment methods).</p>
                        <p>- Free shipping on all orders.</p>

                        {currentOrder.coupons && currentOrder.coupons.filter(coupon => coupon.discount_flat === 0 && coupon.description).length > 0 && (
                            <div style={{ textAlign: "right", fontWeight: "bold", margin: "10px 0" }}>
                                {currentOrder.coupons.filter(coupon => coupon.discount_flat === 0 && coupon.description).map((coupon, idx) => (
                                    <div key={idx}>{coupon.description}</div>
                                ))}
                            </div>
                        )}
                    </div>
                </Modal>

            )
            }

        </div>
    );
};

export default Orders;
